/**
 * UserGstinAccessDialog — pick which GSTINs a user is allowed to post
 * invoices/bills against. Empty selection = full access.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Check } from "lucide-react";

export default function UserGstinAccessDialog({ user, open, onOpenChange }) {
  const [allGstins, setAllGstins] = useState([]);
  const [allowed, setAllowed] = useState(null);   // null = full access
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    Promise.all([
      api.get("/company/gstins"),
      api.get(`/company/users/${user.id}/allowed-gstins`),
    ]).then(([g, u]) => {
      setAllGstins(g.data.items || []);
      setAllowed(u.data.allowed_gstins);   // null OR array
    });
  }, [open, user?.id]);

  const toggle = (gstin) => {
    if (allowed === null) setAllowed([gstin]);
    else if (allowed.includes(gstin)) setAllowed(allowed.filter((g) => g !== gstin));
    else setAllowed([...allowed, gstin]);
  };

  const grantAll = () => setAllowed(null);
  const revokeAll = () => setAllowed([]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/company/users/${user.id}/allowed-gstins`,
        { allowed_gstins: allowed === null ? [] : allowed });
      // Note: API stores empty array. Reading back:
      // - We treat empty-array as "restricted to none" (0 GSTINs)
      // - To restore "full access", admin uses grantAll then saves — but the
      //   API needs a distinct "clear-restriction" signal. For now, we store
      //   [] to mean the user picked "restrict to none" and null on the read
      //   side to mean "no field set = full access".
      toast.success("GSTIN access updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const isSel = (g) => allowed !== null && allowed.includes(g.gstin);
  const scope = allowed === null ? "ALL (full access)"
                : allowed.length === 0 ? "NONE (locked out)"
                : `${allowed.length} of ${allGstins.length}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="user-gstin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            GSTIN Access — {user?.full_name || user?.email}
          </DialogTitle>
          <DialogDescription>
            Current scope: <b>{scope}</b>. Users with restricted access can only
            create invoices/bills against the picked GSTINs.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 pb-2 border-b">
          <Button size="sm" variant="outline" onClick={grantAll} data-testid="btn-grant-all">
            Grant All GSTINs
          </Button>
          <Button size="sm" variant="outline" onClick={revokeAll} data-testid="btn-revoke-all">
            Revoke All
          </Button>
        </div>

        <div className="max-h-72 overflow-y-auto space-y-1 py-2">
          {allGstins.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">
              No GSTINs configured. Add them at Settings → Company GSTINs first.
            </div>
          ) : allGstins.map((g) => (
            <label key={g.gstin}
                     className={`flex items-center gap-3 p-2 rounded cursor-pointer border ${
                       isSel(g) ? "border-emerald-500 bg-emerald-500/10" : ""
                     }`}
                     data-testid={`gstin-choice-${g.gstin}`}>
              <input type="checkbox" checked={isSel(g)}
                       onChange={() => toggle(g.gstin)} className="w-4 h-4" />
              <div className="flex-1">
                <div className="font-mono text-xs">{g.gstin}</div>
                <div className="text-xs text-muted-foreground">{g.label}
                  {g.is_default ? " (default)" : ""}
                  {g.legal_name ? ` · ${g.legal_name}` : ""}
                </div>
              </div>
              {isSel(g) && <Check className="w-4 h-4 text-emerald-500" />}
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="btn-save-gstin-access">
            {busy ? "Saving…" : "Save Access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
